import ExitToAppRoundedIcon from "@mui/icons-material/ExitToAppRounded";
import HelpRoundedIcon from "@mui/icons-material/HelpRounded";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import PersonIcon from "@mui/icons-material/Person";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import {
  AppBar,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem as MuiMenuItem,
  Stack,
  Toolbar,
} from "@mui/material";
import { Link } from "@tanstack/react-router";
import { Fragment, forwardRef, type ReactNode, useState } from "react";
import { useAuth } from "react-oidc-context";
import Logo from "@/assets/wiseline-color.png";
import UploadInfo from "@/components/Overview/UploadInfo";
import { useCompany } from "@/hooks/Companies";
import { OrganizationPicker } from "@/iam/OrganizationPicker";
import { allNavLinks, HeaderNav, type NavSubGroup } from "./HeaderNav";

const isNavSubGroup = (item: unknown): item is NavSubGroup =>
  typeof item === "object" && item !== null && "subLabel" in item;

export const SIDEBAR_BUTTON_WIDTH = 90;

type MenuItemProps = {
  label: string;
  onClick: () => void;
  url?: string;
  icon: ReactNode;
};

const MenuItem = forwardRef(
  ({ label, onClick, url, icon }: MenuItemProps, ref: React.ForwardedRef<HTMLLIElement>) => (
    <MuiMenuItem onClick={onClick} ref={ref}>
      <ListItemIcon>{icon}</ListItemIcon>
      {url ? (
        <Link style={{ textDecoration: "none", color: "#000000" }} to={url}>
          <ListItemText primary={label} />
        </Link>
      ) : (
        <ListItemText primary={label} />
      )}
    </MuiMenuItem>
  ),
);
MenuItem.displayName = "MenuItem";

const Header = () => {
  const { signoutRedirect, user, isAuthenticated, isLoading, error } = useAuth();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | undefined>();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleCloseMenu = () => setAnchorEl(undefined);

  const { selectedCompany, companies } = useCompany();

  const userName = user?.profile?.name || user?.profile?.preferred_username || "Bruker";

  // Must be super strict to avoid auth mismatch and timining issues.
  const showAuthUI = Boolean(user) && isAuthenticated && !isLoading && !error && !user?.expired;

  return (
    <AppBar
      elevation={0}
      position="relative"
      sx={{
        backgroundColor: (theme) => theme.vars.palette.background.paper,
        borderBottom: (theme) => `1px solid ${theme.vars.palette.divider}`,
      }}
    >
      <Toolbar sx={{ justifyContent: "space-between" }}>
        <Link to="/" style={{ textDecoration: "none" }}>
          <Box
            alt="Wiseline logo"
            component="img"
            src={Logo}
            sx={{
              width: "93px",
              borderRadius: 1,
              transition: "filter 0.2s, box-shadow 0.2s",
              padding: "3px",
              filter: "drop-shadow(0px 2px 0 rgba(0,0,0,0.12))",
              "&:hover": {
                filter: "drop-shadow(0px 3px 0 rgba(0,0,0,0.2))",
              },
            }}
          />
        </Link>

        <Box sx={{ display: { xs: "none", md: "flex" } }}>
          {showAuthUI && selectedCompany ? <HeaderNav /> : null}
        </Box>

        <Stack
          direction="row"
          sx={{
            gap: 2,
            alignItems: "center",
          }}
        >
          <Box sx={{ display: { xs: "none", md: "block" } }}>
            {showAuthUI && selectedCompany ? <UploadInfo /> : null}
          </Box>
          <Button
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{ color: "text.secondary" }}
            startIcon={
              <Box
                sx={{
                  bgcolor: "primary.main",
                  borderRadius: 0.5,
                  px: 1,
                  py: 0.5,
                  lineHeight: 0.6,
                }}
              >
                <PersonIcon
                  htmlColor="white"
                  sx={{
                    border: (theme) => `1.5px solid ${theme.palette.primary.contrastText}`,
                    borderRadius: "50%",
                  }}
                />
              </Box>
            }
          >
            {selectedCompany ? `${userName} - ${selectedCompany?.name}` : userName}
          </Button>
          <IconButton
            aria-label="Åpne navigasjonsmeny"
            onClick={() => setMobileMenuOpen(true)}
            sx={{
              display: { xs: "flex", md: "none" },
              color: "text.secondary",
            }}
          >
            <MenuRoundedIcon />
          </IconButton>
        </Stack>

        <Menu
          anchorEl={anchorEl}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          disableAutoFocusItem
          disableScrollLock
          elevation={0}
          keepMounted
          onClose={handleCloseMenu}
          open={Boolean(anchorEl)}
          // TODO: Use slotProps.paper instead
          slotProps={{
            paper: {
              sx: {
                border: "1px solid #d3d4d5",
                width: "235px",
                marginTop: "4px",
              },
            },
          }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
        >
          {(companies ?? []).length > 1 && <OrganizationPicker />}
          <MenuItem
            icon={<SettingsRoundedIcon fontSize="small" />}
            label="Innstillinger"
            onClick={handleCloseMenu}
            url="/innstillinger"
          />
          <MenuItem
            icon={<HelpRoundedIcon fontSize="small" />}
            label="Hjelp og støtte"
            onClick={handleCloseMenu}
            url="/hjelp"
          />
          <MenuItem
            icon={<ExitToAppRoundedIcon fontSize="small" />}
            label="Logg ut"
            onClick={() => {
              handleCloseMenu();
              void signoutRedirect();
            }}
          />
        </Menu>
        <Drawer
          anchor="left"
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          slotProps={{ paper: { sx: { width: 280 } } }}
        >
          {showAuthUI && selectedCompany && (
            <Box sx={{ p: 2 }}>
              <UploadInfo />
            </Box>
          )}
          <Divider />
          <List disablePadding>
            {allNavLinks.map((entry) =>
              "items" in entry ? (
                <Fragment key={entry.label}>
                  <ListSubheader sx={{ lineHeight: "36px" }}>{entry.label}</ListSubheader>
                  {entry.items.map((item) =>
                    isNavSubGroup(item) ? (
                      <Fragment key={item.subLabel}>
                        <ListSubheader sx={{ lineHeight: "28px", pl: 4 }}>
                          {item.subLabel}
                        </ListSubheader>
                        {item.items.map((subItem) => (
                          <ListItem disablePadding key={subItem.linkOptions.to}>
                            <Link
                              {...subItem.linkOptions}
                              onClick={() => setMobileMenuOpen(false)}
                              style={{
                                display: "block",
                                width: "100%",
                                textDecoration: "none",
                                color: "inherit",
                              }}
                            >
                              <ListItemButton dense sx={{ pl: 4 }} tabIndex={-1}>
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                  <subItem.Icon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText primary={subItem.label} />
                              </ListItemButton>
                            </Link>
                          </ListItem>
                        ))}
                      </Fragment>
                    ) : (
                      <ListItem disablePadding key={item.linkOptions.to}>
                        <Link
                          {...item.linkOptions}
                          onClick={() => setMobileMenuOpen(false)}
                          style={{
                            display: "block",
                            width: "100%",
                            textDecoration: "none",
                            color: "inherit",
                          }}
                        >
                          <ListItemButton dense tabIndex={-1}>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <item.Icon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText primary={item.label} />
                          </ListItemButton>
                        </Link>
                      </ListItem>
                    ),
                  )}
                </Fragment>
              ) : (
                <ListItem disablePadding key={entry.linkOptions.to}>
                  <Link
                    {...entry.linkOptions}
                    onClick={() => setMobileMenuOpen(false)}
                    style={{
                      display: "block",
                      width: "100%",
                      textDecoration: "none",
                      color: "inherit",
                    }}
                  >
                    <ListItemButton dense tabIndex={-1}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <entry.Icon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={entry.label} />
                    </ListItemButton>
                  </Link>
                </ListItem>
              ),
            )}
          </List>
        </Drawer>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
